import React, { useState, useEffect } from 'react';
import { PlusIcon, TrashIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';

const SimpleCampusFacilities = ({ profileData }) => {
 const [facilities, setFacilities] = useState([]);
 const [newFacility, setNewFacility] = useState({
 facility_name: '',
 facility_description: ''
 });
 const [loading, setLoading] = useState(false);
 const [adding, setAdding] = useState(false);
 const [error, setError] = useState(null);

 // Simple fetch without calling external functions
 const fetchFacilities = async () => {
 if (!profileData?.id) return;

 setLoading(true);
 try {
 const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
 const token = localStorage.getItem('token');
 const response = await fetch(`${apiUrl}/university/facilities`, {
 headers: {
 'Authorization': `Bearer ${token}`
 }
 });

 if (response.ok) {
 const data = await response.json();
 setFacilities(data);
 } else {
 console.error('Failed to fetch facilities');
 }
 } catch (error) {
 console.error('Error fetching facilities:', error);
 } finally {
 setLoading(false);
 }
 };

 useEffect(() => {
 fetchFacilities();
 }, [profileData?.id]);

 const handleInputChange = (e) => {
 const { name, value } = e.target;
 setNewFacility(prev => ({
 ...prev,
 [name]: value
 }));
 };

 const handleAddFacility = async (e) => {
 e.preventDefault();

 if (!newFacility.facility_name.trim()) {
 setError('Facility name is required');
 return;
 }

 setAdding(true);
 setError(null);

 try {
 const token = localStorage.getItem('token');
 const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
 const response = await fetch(`${apiUrl}/university/facilities`, {
 method: 'POST',
 headers: {
 'Content-Type': 'application/json',
 'Authorization': `Bearer ${token}`
 },
 body: JSON.stringify(newFacility)
 });

 if (response.ok) {
 const result = await response.json();
 setFacilities(prev => [...prev, result.facility]);
 setNewFacility({ facility_name: '', facility_description: '' });
 // Don't call external functions - just refresh our own data
 fetchFacilities();
 } else {
 const errorData = await response.json();
 setError(errorData.error || 'Failed to add facility');
 }
 } catch (error) {
 console.error('Error adding facility:', error);
 setError('Failed to add facility');
 } finally {
 setAdding(false);
 }
 };

 const handleDeleteFacility = async (facilityId) => {
 if (!window.confirm('Are you sure you want to delete this facility?')) {
 return;
 }

 try {
 const token = localStorage.getItem('token');
 const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
 const response = await fetch(`${apiUrl}/university/facilities/${facilityId}`, {
 method: 'DELETE',
 headers: {
 'Authorization': `Bearer ${token}`
 }
 });

 if (response.ok) {
 setFacilities(prev => prev.filter(f => f.id !== facilityId));
 // Don't call external functions - just refresh our own data
 fetchFacilities();
 } else {
 const errorData = await response.json();
 setError(errorData.error || 'Failed to delete facility');
 }
 } catch (error) {
 console.error('Error deleting facility:', error);
 setError('Failed to delete facility');
 }
 };

 if (!profileData) {
 return (
 <div className="card">
 <p className="text-slate-500 font-medium text-sm">Loading profile data...</p>
 </div>
 );
 }

 return (
 <div className="card">
 <div className="flex items-center justify-between mb-6">
 <h3 className="text-lg font-bold text-slate-800 flex items-center">
 <BuildingOfficeIcon className="h-5 w-5 text-primary-600 mr-2" />
 Campus Facilities
 </h3>
 <span className="text-xs font-semibold text-slate-400 uppercase bg-slate-100 border border-slate-200 px-2 py-1 rounded-lg">
 {facilities.length} {facilities.length === 1 ? 'facility' : 'facilities'}
 </span>
 </div>

 {/* Error Message */}
 {error && (
 <div className="mb-4 p-4 bg-rose-50/50 border border-rose-200 rounded-xl">
 <p className="text-sm text-rose-700 font-medium">{error}</p>
 </div>
 )}

 {/* Add New Facility Form */}
 <form onSubmit={handleAddFacility} className="mb-6 p-5 bg-slate-50 border border-slate-200 rounded-2xl">
 <h4 className="text-sm font-bold text-slate-700 mb-3">Add New Facility</h4>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
 <div>
 <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
 Facility Name *
 </label>
 <input
 type="text"
 name="facility_name"
 value={newFacility.facility_name}
 onChange={handleInputChange}
 placeholder="e.g., Central Library, Computer Lab"
 className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 bg-white text-slate-700 transition-all duration-300"
 required
 />
 </div>

 <div>
 <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
 Description
 </label>
 <input
 type="text"
 name="facility_description"
 value={newFacility.facility_description}
 onChange={handleInputChange}
 placeholder="Brief description of the facility"
 className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 bg-white text-slate-700 transition-all duration-300"
 />
 </div>
 </div>

 <button
 type="submit"
 disabled={adding}
 className="btn-primary flex items-center justify-center py-2 px-5 text-sm disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
 >
 <PlusIcon className="h-4 w-4 mr-2" />
 <span>{adding ? 'Adding...' : 'Add Facility'}</span>
 </button>
 </form>

 {/* Facilities List */}
 {loading ? (
 <div className="text-center py-6">
 <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
 <p className="text-slate-500 mt-2 text-sm font-semibold">Loading facilities...</p>
 </div>
 ) : facilities.length === 0 ? (
 <div className="text-center py-10 text-slate-400 bg-slate-50 border border-slate-100 rounded-2xl">
 <BuildingOfficeIcon className="h-12 w-12 mx-auto mb-3 text-slate-300" />
 <p className="font-bold text-slate-700">No Facilities Added</p>
 <p className="text-sm mt-1">Add your campus facilities using the form above.</p>
 </div>
 ) : (
 <div className="space-y-3">
 {facilities.map((facility) => (
 <div
 key={facility.id}
 className="flex items-start justify-between p-4 bg-slate-50 border border-slate-100 rounded-xl hover:bg-slate-100 transition-colors"
 >
 <div className="flex-1 min-w-0">
 <h4 className="font-bold text-slate-700 truncate">{facility.facility_name}</h4>
 {facility.facility_description && (
 <p className="text-sm text-slate-500 mt-1 leading-relaxed">{facility.facility_description}</p>
 )}
 </div>

 <button
 onClick={() => handleDeleteFacility(facility.id)}
 className="ml-4 p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-xl transition-all active:scale-95 border border-transparent hover:border-red-100"
 title="Delete facility"
 >
 <TrashIcon className="h-4.5 w-4.5" />
 </button>
 </div>
 ))}
 </div>
 )}
 </div>
 );
};

export default SimpleCampusFacilities;
